// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useState } from 'react';
import { Button, InputBase, IconButton } from '@mui/material';
import { Box, useTheme } from '@mui/system';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import { useAppDispatch } from 'Hooks';
import stylesDef from './styles';
import { updateFolderThunk } from '../folderListThunk';

interface ComponentProps {
  initialFolderName: string;
  folderId: string;
}

export const FolderName: React.FC<ComponentProps> = ({ initialFolderName, folderId }) => {
  // Constants
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  // State variables
  const [folderName, setFolderName] = useState(null);
  const [isFolderNameEditable, setIsFolderNameEditable] = useState(false);

  // Methods
  const startEditingFolderName = () => {
    setFolderName(initialFolderName);
    setIsFolderNameEditable(true);
  };

  const updateFolderName = async () => {
    // API call to update the folder name
    const response = await dispatch(updateFolderThunk(folderId, folderName));

    if (response.success) {
      // Close the edit mode
      setIsFolderNameEditable(false);
    }
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const AllowEdit = () => {
    if (isFolderNameEditable) {
      return (
        <IconButton aria-label="check" onClick={updateFolderName}>
          <CheckIcon />
        </IconButton>
      );
    }
    return (
      <IconButton aria-label="edit" onClick={startEditingFolderName}>
        <EditIcon />
      </IconButton>
    );
  };

  return (
    <Box sx={styles.projectName}>
      {!isFolderNameEditable ? (
        <Button
          sx={styles.titleButton}
          variant="text"
          disableTouchRipple
          onDoubleClick={startEditingFolderName}
        >
          <span>{folderName || initialFolderName}</span>
        </Button>
      ) : (
        <InputBase
          sx={styles.inputField}
          autoFocus
          value={folderName}
          onChange={(e) => {
            setFolderName(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === 'Escape') {
              // check if folder name has changed
              if (!folderName || folderName.trim() === initialFolderName) {
                setIsFolderNameEditable(false);
                setFolderName(null);
                return;
              }
              // else call the update folder name API
              updateFolderName();
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        />
      )}
      {AllowEdit()}
    </Box>
  );
};

export default FolderName;
